import React, { useState, useMemo, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Order, statusOrder } from '../types';
import { useUserRole } from '../../auth/hooks';
import { CSF_URL } from '../../common/utils/util';
import { TableHeader } from '../../common';
import './orders-table.css';

interface OrdersTableProps {
  orders: Order[];
  loading: boolean;
  onDelete?: (id: number) => Promise<boolean>;
}

// Definición de la configuración de columnas
interface ColumnConfig {
  field: keyof Order | 'actions' | 'view_csf';
  header: string;
  sortable: boolean;
  style: Object;
  body?: (rowData: Order) => React.ReactNode;
  restrictedRoles?: string[];
}

// Template para la columna de estado
const statusTemplate = (rowData: Order) => {
  const status = rowData.status as statusOrder;
  const statusLabel = status === statusOrder.PENDING ? 'Pendiente' : 'Verificado';
  const statusClass = status === statusOrder.PENDING ? 'status-pending' : 'status-verified';
  
  return (
    <span className={`status-badge ${statusClass}`}>
      {statusLabel}
    </span>
  );
};

// Template para la columna de productos
const productsTemplate = (rowData: Order) => {
  if (!rowData.lineItems || rowData.lineItems.length === 0) {
    return <span style={{ color: '#999' }}>N/A</span>;
  }
  
  return (
    <div className="products-list">
      {rowData.lineItems.map((item, index) => (
        <div key={index} className="product-item">
          <span className="product-title">{item.title}</span>
          <span className="product-quantity">x{item.quantity}</span>
        </div>
      ))}
    </div>
  );
};

// Template para la columna de precio
const priceTemplate = (rowData: Order) => {
  if (!rowData.totalPrice) {
    return <span style={{ color: '#999' }}>N/A</span>;
  }
  
  const numericPrice = parseFloat(rowData.totalPrice);
  if (isNaN(numericPrice)) {
    return <span style={{ color: '#999' }}>N/A</span>;
  }
  
  const formattedPrice = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numericPrice);
  
  return <span>{formattedPrice}</span>;
};

// Template para la columna de correo
const emailTemplate = (rowData: Order) => {
  if (!rowData.customerEmail) {
    return <span style={{ color: '#999' }}>N/A</span>;
  }
  
  return (
    <a
      href={`mailto:${rowData.customerEmail}`}
      style={{ 
        color: "#007ad9", 
        textDecoration: "none",
        fontSize: "0.875rem"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.textDecoration = "underline";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.textDecoration = "none";
      }}
      title="Enviar correo electrónico"
    >
      {rowData.customerEmail}
    </a>
  );
};

// Template para la columna de celular
const phoneTemplate = (rowData: Order) => {
  const phone = rowData.shippingAddress?.phone;
  if (!phone) {
    return <span style={{ color: '#999' }}>N/A</span>;
  }
  
  // Limpiar el número de teléfono (remover espacios, guiones, paréntesis)
  const cleanPhone = phone.replace(/[\s\-()]/g, '');
  
  // Verificar si el número tiene el código de país de México (+52)
  const formattedPhone = cleanPhone.startsWith('+52') 
    ? cleanPhone 
    : cleanPhone.startsWith('52') 
      ? `+${cleanPhone}`
      : `+52${cleanPhone}`;
  
  const whatsappUrl = `https://wa.me/${formattedPhone.replace('+', '')}`;
  
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{ 
        color: "#25D366", 
        textDecoration: "none",
        fontSize: "0.875rem",
        fontWeight: "500"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.textDecoration = "underline";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.textDecoration = "none";
      }}
      title="Enviar mensaje por WhatsApp"
    >
      {phone}
    </a>
  );
};

// Template para la columna de ver constancia
const viewCsfTemplate = (rowData: Order) => {
  if (!rowData.csf) {
    return <span style={{ color: '#999' }}>N/A</span>;
  }
  
  return (
    <a
      href={`${CSF_URL}${rowData.csf}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ 
        color: "black", 
        fontWeight: "bold", 
        textDecoration: "underline",
        fontSize: "0.875rem"
      }}
    >
      Ver Constancia
    </a>
  );
};

// Template para la columna de acciones
const createActionsTemplate = (
  onDelete: (id: number) => Promise<boolean>,
  showSuccess: (message: string) => void,
  showError: (message: string) => void
) => {
  return (rowData: Order) => {
    const handleDelete = () => {
      confirmDialog({
        message: `¿Estás seguro de eliminar el pedido #${rowData.orderNumber} con el correo ${rowData.customerEmail}?`,
        header: 'Confirmar Eliminación',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sí, eliminar',
        rejectLabel: 'No, cancelar',
        acceptClassName: 'p-button-danger',
        accept: async () => {
          const success = await onDelete(rowData.id);
          if (success) {
            showSuccess('Orden eliminada correctamente');
          } else {
            showError('No se pudo eliminar la orden');
          }
        }
      });
    };

    return (
      <div className="action-buttons">
        <Button 
          icon="pi pi-trash" 
          className="p-button-rounded p-button-text p-button-danger" 
          onClick={handleDelete} 
          tooltip="Eliminar"
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    );
  };
};

// Configuración de columnas definida fuera del componente
const COLUMN_CONFIGS: ColumnConfig[] = [
  {
    field: 'shopifyOrderId',
    header: 'Id de Pedido',
    sortable: true,
    style: { minWidth: '120px' }
  },
  {
    field: 'orderNumber',
    header: 'Pedido',
    sortable: true,
    style: { maxWidth: '80px' }
  },
  {
    field: 'lineItems',
    header: 'Productos',
    sortable: false,
    style: { minWidth: '200px', maxWidth: '300px' },
    body: productsTemplate
  },
  {
    field: 'totalPrice',
    header: 'Precio Total',
    sortable: true,
    style: { minWidth: '120px' },
    body: priceTemplate
  },
  {
    field: 'customerEmail',
    header: 'Correo',
    sortable: true,
    style: { minWidth: '150px', maxWidth: '250px' },
    body: emailTemplate
  },
  {
    field: 'shippingAddress',
    header: 'Celular',
    sortable: false,
    style: { minWidth: '120px' },
    body: phoneTemplate
  },
  {
    field: 'shippingAddress',
    header: 'Estado',
    sortable: true,
    style: { minWidth: '100px' },
    body: (rowData: Order) => {
      return rowData.shippingAddress?.province || <span style={{ color: '#999' }}>N/A</span>;
    }
  },
  {
    field: 'status',
    header: 'Estado Orden',
    sortable: true,
    style: { minWidth: '100px' },
    body: statusTemplate
  },
  {
    field: 'view_csf',
    header: 'Ver Constancia',
    sortable: false,
    style: { minWidth: '120px' },
    body: viewCsfTemplate
  },
  {
    field: 'actions',
    header: 'Acciones',
    sortable: false,
    style: { minWidth: '100px', textAlign: 'center' }
  }
];

const OrdersTable: React.FC<OrdersTableProps> = ({ orders, loading, onDelete }) => {
  const { userRole } = useUserRole();
  const toast = useRef<Toast>(null);
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('');

  // Funciones para mostrar mensajes Toast
  const showSuccess = useCallback((message: string) => {
    toast.current?.show({
      severity: 'success',
      summary: 'Éxito',
      detail: message,
      life: 3000
    });
  }, []);

  const showError = useCallback((message: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 3000
    });
  }, []);

  // Filtrar columnas según el rol del usuario
  const visibleColumns = useMemo(() => {
    const columns = COLUMN_CONFIGS.filter(column => {
      if (!column.restrictedRoles) return true;
      return userRole ? column.restrictedRoles.includes(userRole) : false;
    });

    // Si hay función onDelete, agregar el body para la columna de acciones
    if (onDelete) {
      return columns.map(column => {
        if (column.field === 'actions') {
          return {
            ...column,
            body: createActionsTemplate(onDelete, showSuccess, showError)
          };
        }
        return column;
      });
    }

    // Si no hay onDelete, filtrar la columna de acciones
    return columns.filter(column => column.field !== 'actions');
  }, [userRole, onDelete, showSuccess, showError]);

  // Función para manejar el cambio del filtro global
  const onGlobalFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalFilterValue(e.target.value);
  }, []);

  // Componente para el header con filtro global
  const header = useMemo(() => {
    return <TableHeader 
      globalFilterValue={globalFilterValue} 
      onGlobalFilterChange={onGlobalFilterChange} 
    />;
  }, [globalFilterValue, onGlobalFilterChange]);

  // Template para cuando no hay datos
  const emptyMessageTemplate = () => {
    return (
      <div className="empty-message">
        <i className="pi pi-inbox" style={{ fontSize: '2rem', color: '#ccc' }}></i>
        <p>No se encontraron órdenes</p>
      </div>
    );
  };

  // Obtener los campos de filtro global según las columnas visibles
  const getGlobalFilterFields = useCallback((columns: ColumnConfig[]) => {
    return columns.map(column => column.field);
  }, []);

  // Obtener los campos de filtro global según las columnas visibles
  const globalFilterFields = useMemo(() => {
    return getGlobalFilterFields(visibleColumns);
  }, [visibleColumns, getGlobalFilterFields]);

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="orders-table-container">
        <DataTable
          value={orders}
          loading={loading}
          emptyMessage={emptyMessageTemplate}
          header={header}
          globalFilter={globalFilterValue}
          globalFilterFields={globalFilterFields}
          paginator
          rows={20}
          rowsPerPageOptions={[20, 50, 100, 200]}
          sortMode="multiple"
          removableSort
          resizableColumns
          columnResizeMode="expand"
          showGridlines
          stripedRows
          className="orders-datatable"
          tableStyle={{ minWidth: '50rem' }}
        >
          {visibleColumns.map((config) => (
            <Column
              key={config.field}
              field={config.field}
              header={config.header}
              sortable={config.sortable}
              style={config.style}
              body={config.body}
            />
          ))}
        </DataTable>
      </div>
    </>
  );
};

export default OrdersTable;
